import { Link, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { bawabahCallback } from "@/api/auth";
import { Button } from "@/components/ui/button";
import { setAccessToken } from "@/config";
import { useLanguage } from "@/contexts/language";
import { needsOnboarding } from "@/features/onboarding/onboarding-page";
import { setLanguage } from "@/i18n";
import { errorMessage } from "@/lib/errors";

import { loadSession } from "@/session";

export function AuthCallbackPage({ sessionId }: { sessionId?: string }) {
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!sessionId) {
      setError(t("Mandoob:Auth.MissingSession"));
      return;
    }

    (async () => {
      try {
        const result = await bawabahCallback({ sessionId });
        setAccessToken(result.accessToken);
        const session = await loadSession();
        // First boot: the driver's server-side language preference wins only
        // when no local preference is stored.
        try {
          if (!localStorage.getItem("mandoob:language")) {
            setLanguage(session.language, false);
          }
        } catch {
          // storage unavailable
        }
        // Bare profile on first sign-in → one-time onboarding instead of home.
        void navigate({
          to: needsOnboarding(session.driver) ? "/onboarding" : "/home",
          replace: true,
        });
      } catch (err) {
        setAccessToken(null);
        setError(errorMessage(err, t, "Mandoob:Auth.Failed"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (error) {
    return (
      <div
        dir={dir}
        className="tw:flex tw:min-h-dvh tw:flex-col tw:items-center tw:justify-center tw:gap-4 tw:p-8 tw:text-center"
      >
        <p className="tw:m-0 tw:text-lg tw:font-semibold">
          {t("Mandoob:Auth.FailedTitle")}
        </p>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">{error}</p>
        <Button asChild variant="outline">
          <Link to="/login">{t("Mandoob:Auth.BackToLogin")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div
      dir={dir}
      className="tw:flex tw:min-h-dvh tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:p-8"
    >
      <Loader2 className="tw:size-6 tw:animate-spin tw:text-muted-foreground" />
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        {t("Mandoob:Auth.SigningIn")}
      </p>
    </div>
  );
}
